import type { EventDetail } from '../services/api';
import { teamFlag } from '../utils/flags';
import { matchPhase, countdownTo, sinceKickoff, useNow } from '../utils/matchTiming';

interface LiveScoreTrackerProps {
  event: EventDetail;
}

export default function LiveScoreTracker({ event }: LiveScoreTrackerProps) {
  useNow(1000); // tick so the countdown updates every second

  // A finished status from the backend wins; otherwise derive from kick-off time.
  const phase = event.status === 'finished' ? 'ended' : matchPhase(event.starts_at);
  const hasScore = event.home_score !== null && event.away_score !== null;

  const kickoffAbs = event.starts_at
    ? new Date(event.starts_at).toLocaleString('en-ZA', {
        weekday: 'short', day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit',
      })
    : '';

  return (
    <div className="rounded-card border border-border bg-surface p-6 shadow-card text-center">
      {/* Teams + score row */}
      <div className="flex items-center justify-center gap-4 sm:gap-8">
        {/* Home team */}
        <div className="flex flex-1 min-w-0 flex-col items-center gap-1">
          <span className="rounded-pill bg-primary/10 px-2 py-0.5 text-[10px] font-bold uppercase tracking-wide text-primary">
            🏠 Home
          </span>
          {teamFlag(event.home_team) && (
            <span className="text-3xl leading-none" aria-hidden="true">{teamFlag(event.home_team)}</span>
          )}
          <p className="truncate font-heading text-lg font-bold text-text-primary sm:text-xl">
            {event.home_team}
          </p>
        </div>

        {/* Score / countdown block */}
        <div className="flex shrink-0 flex-col items-center gap-1.5">
          {hasScore ? (
            <div className="flex items-center gap-3">
              <span className="font-heading text-3xl font-bold text-text-primary tabular-nums">{event.home_score}</span>
              <span className="font-heading text-xl font-bold text-text-muted">:</span>
              <span className="font-heading text-3xl font-bold text-text-primary tabular-nums">{event.away_score}</span>
            </div>
          ) : (
            <span className="font-heading text-2xl font-bold text-text-muted">vs</span>
          )}

          {phase === 'upcoming' && (
            <span className="flex flex-col items-center gap-0.5">
              <span className="rounded-pill bg-primary/10 px-2.5 py-0.5 text-xs font-bold text-primary">
                ⏱ Starts in {countdownTo(event.starts_at)}
              </span>
            </span>
          )}
          {phase === 'inplay' && (
            <span className="rounded-pill bg-cta/10 px-2.5 py-0.5 text-xs font-bold text-cta">
              <span className="mr-1 inline-block h-1.5 w-1.5 animate-pulse rounded-full bg-cta align-middle" />
              In play
            </span>
          )}
          {phase === 'ended' && (
            <span className="rounded-pill bg-surface-2 px-2.5 py-0.5 text-xs font-bold text-text-muted">
              Match ended
            </span>
          )}
        </div>

        {/* Away team */}
        <div className="flex flex-1 min-w-0 flex-col items-center gap-1">
          <span className="rounded-pill bg-surface-2 px-2 py-0.5 text-[10px] font-bold uppercase tracking-wide text-text-muted">
            ✈ Away
          </span>
          {teamFlag(event.away_team) && (
            <span className="text-3xl leading-none" aria-hidden="true">{teamFlag(event.away_team)}</span>
          )}
          <p className="truncate font-heading text-lg font-bold text-text-primary sm:text-xl">
            {event.away_team}
          </p>
        </div>
      </div>

      {/* Plain-language home/away note for newcomers */}
      <p className="mt-3 text-xs text-text-muted">
        <span className="font-semibold text-text-secondary">{event.home_team}</span> play at home ·{' '}
        <span className="font-semibold text-text-secondary">{event.away_team}</span> are the visiting side
      </p>

      {/* League + kick-off / phase line */}
      <p className="mt-3 text-sm text-text-muted">
        {event.league}
        {event.starts_at && <> · {kickoffAbs}</>}
      </p>

      {phase === 'inplay' && !hasScore && (
        <p className="mt-1 text-xs text-text-muted">
          Kicked off {sinceKickoff(event.starts_at)} — live scores aren't shown here, follow on the broadcast.
        </p>
      )}
    </div>
  );
}
